<template>
  <div class="p-6 max-w-6xl mx-auto space-y-6">
    <el-card shadow="hover">
      <template #header>
  <div class="flex items-start md:items-center justify-between flex-col md:flex-row gap-3">
          <h2 class="text-lg font-semibold">{{ isMobile() ? 'IRR比较' : '实际利率（IRR）比较' }}</h2>
        </div>
      </template>

      <el-alert type="info" :closable="false" class="mb-3" title="用途：在考虑一次性/每期费用后，计算贷款的真实成本（周期IRR与有效年化），用于比较不同贷款方案的实际利率。" />
      <FormulaPanel
        title="IRR 定义与现金流构造"
        :formulas="[irrFormula, cashflowFormula]"
        :variables="irrVars"
        :notes="['IRR为使NPV=0的收益率','借款人视角：t0净流入、其后每期为流出']"
      />
  <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-4 grid grid-cols-1 md:grid-cols-5 gap-4">
          <el-form-item :label="`贷款金额（${symbol}）`">
            <el-input v-model.number="amountUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item label="名义年化（%）">
            <el-input v-model.number="form.annualRatePct" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写APR）</div>
          </el-form-item>
          <el-form-item label="期数（月）">
            <el-input v-model.number="form.months" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
          <el-form-item :label="`一次性费用（${symbol}）`">
            <el-input v-model.number="upfrontFeeUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写F₀）</div>
          </el-form-item>
          <el-form-item :label="`每期费用（${symbol}）`">
            <el-input v-model.number="periodicFeeUI" type="number" />
            <div class="text-xs text-gray-500 mt-1">（此处填写f）</div>
          </el-form-item>
        </el-form>
  <div class="md:col-span-1 flex gap-3 md:justify-end flex-wrap">
          <el-button type="primary" @click="onCalc">计算</el-button>
          <el-button @click="onReset">重置</el-button>
        </div>
      </div>

      <el-alert v-if="error" type="error" class="mt-4" :closable="false" :title="error" />

      <el-collapse-transition>
        <div v-if="result" class="mt-4 space-y-4">
  <el-descriptions title="结果" :column="3" border>
          <el-descriptions-item label="名义年化(输入)">{{ form.annualRatePct }}%</el-descriptions-item>
          <el-descriptions-item label="周期IRR(%)">{{ result.periodicIrrPct }}</el-descriptions-item>
          <el-descriptions-item label="有效年化(%)">{{ result.effectiveAnnualPct }}</el-descriptions-item>
          <el-descriptions-item :label="`月供（${symbol}）`">{{ d2(result.pmt) }}</el-descriptions-item>
        </el-descriptions>

        <div class="overflow-x-auto">
        <el-table :data="result.cashflows.map((c, i) => ({i, c: Number(toDisplay(c)).toFixed(2)}))" size="small">
          <el-table-column prop="i" label="期" width="80" />
          <el-table-column prop="c" :label="`现金流（${symbol}）`" />
        </el-table>
        </div>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { pmtEqualPI } from '@/lib/loanEngine'
import { irr, buildLoanCashflows, effectiveAnnualFromPeriodic } from '@/lib/tvmEngine'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'
function isMobile() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
const irrFormula = String.raw`\text{NPV}(r) = \sum_{t=0}^{n} \dfrac{CF_t}{(1+r)^t} = 0,\ \ \text{IRR} = r_{\text{root}}`
const cashflowFormula = String.raw`CF_0 = PV - F_0,\ \ CF_t = -(PMT + f),\ t=1..n`
const irrVars = [
  { symbol: 'PV', label: '贷款本金' },
  { symbol: 'PMT', label: '每期还款（等额本息）' },
  { symbol: 'n', label: '期数' },
  { symbol: 'F_0', label: '一次性费用' },
  { symbol: 'f', label: '每期费用' }
]

const form = ref({ amount: 500000, annualRatePct: 4.2, months: 360, upfrontFee: 0, periodicFee: 0 })
const result = ref(null)
const error = ref('')

// 货币换算
const { symbol, toDisplay, toBase } = useCurrency()
const amountUI = computed({ get: () => toDisplay(form.value.amount), set: v => form.value.amount = toBase(v) })
const upfrontFeeUI = computed({ get: () => toDisplay(form.value.upfrontFee), set: v => form.value.upfrontFee = toBase(v) })
const periodicFeeUI = computed({ get: () => toDisplay(form.value.periodicFee), set: v => form.value.periodicFee = toBase(v) })

const onCalc = () => {
  error.value = ''
  result.value = null
  try {
    const { amount, annualRatePct, months, upfrontFee, periodicFee } = form.value
    const r = Number(annualRatePct) / 12 / 100
    const pmt = pmtEqualPI(amount, r, months)
    const flows = buildLoanCashflows({ principal: amount, months, pmt, upfrontFee, periodicFee })
    const rIrr = irr(flows)
    const effAnnual = effectiveAnnualFromPeriodic(rIrr, 12)
    result.value = {
      pmt: Math.round(pmt * 100) / 100,
      periodicIrrPct: Math.round(rIrr * 10000) / 100, // %
      effectiveAnnualPct: Math.round(effAnnual * 10000) / 100, // %
      cashflows: flows
    }
  } catch (e) {
    error.value = e?.message || '计算失败'
  }
}

const onReset = () => {
  result.value = null
  error.value = ''
}

// 显示用：金额保留两位小数
const d2 = (v) => Number(toDisplay(v)).toFixed(2)

</script>
